import type { ArrayValueType, NodeV2, NodeV2Style } from '@vedalib/editor'
import { NodeType, saveBackground } from '@vedalib/editor'
import { setThemeVariable } from '@vedalib/editor/lib/brand'
import type { ElementsTypes } from '@vedalib/editor/lib/elements'
import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import '@vedalib/editor/lib/elements'
import { generateId } from '@vedalib/editor/lib/utils/id'

import { DeviceMode } from 'services/Store/Project/enums'
import type { MapType } from 'services/Store/Project/types'

import { saveArea } from './formControls/Area/utils'
import { saveCoords } from './formControls/Coords/utils'
import { saveGrid } from './formControls/Grid/utils'
import type { ISizeValue } from './formControls/Size/types'
import { saveSize } from './formControls/Size/utils'
import { savePxSizes } from './formControls/Sizes/utils'

export const toArray = <T>(item: T, count: number): T[] => new Array(count).fill(item)
export const toArrayValue = <T>(item: T): ArrayValueType<T> => ({ data: item, value: generateId() })

export const FR_1: ISizeValue = { unit: 'fr', value: 1 }
export const PX_12: ISizeValue = { unit: 'px', value: 12 }
export const PX_20: ISizeValue = { unit: 'px', value: 20 }
export const PX_60: ISizeValue = { unit: 'px', value: 60 }
export const PX_92: ISizeValue = { unit: 'px', value: 92 }
export const PX_98: ISizeValue = { unit: 'px', value: 98 }
export const PX_115: ISizeValue = { unit: 'px', value: 115 }

export const DEFAULT_MIN_SIZE = saveSize({ value: 32, unit: 'px' })
export const DEFAULT_FULL_SIZE = saveSize({ value: 100, unit: '%' })
export const DEFAULT_AUTO_SIZE = saveSize(null)
export const DEFAULT_COMPLEX_SIZE = savePxSizes([null, null, null, null])
export const DEFAULT_GRID_AUTO_ROWS = saveSize(null)
export const DEFAULT_GRID_ITEMS = () =>
  saveGrid({
    min: 32,
    items: [
      { data: { value: 1, unit: 'fr' }, value: generateId() },
      { data: { value: 1, unit: 'fr' }, value: generateId() },
    ],
  })
export const DEFAULT_AREA = saveArea({ rowStart: 1, rowEnd: 2, colStart: 1, colEnd: 2 })
export const DEFAULT_COORDS = saveCoords({ top: 0, left: 0 })

export const DEFAULT_NODE_STYLE_VALUES: NodeV2Style = {
  hidden: false,
  position: 'relative',
  width: DEFAULT_AUTO_SIZE,
  height: DEFAULT_AUTO_SIZE,

  zIndex: 1,
  borderRadius: DEFAULT_COMPLEX_SIZE,
  overflow: 'visible',
  padding: DEFAULT_COMPLEX_SIZE,
  margin: DEFAULT_COMPLEX_SIZE,

  // Grid
  gridColumns: DEFAULT_GRID_ITEMS(),
  gridRows: DEFAULT_GRID_ITEMS(),
  gridAutoRows: DEFAULT_GRID_AUTO_ROWS,
  gridColumnsGap: 2,
  gridRowsGap: 2,

  // GridItem
  gridArea: DEFAULT_AREA,

  // Flex
  flexDirection: 'row',
  flexAlignItems: 'stretch',
  flexJustifyContent: 'flex-start',
  flexBasis: DEFAULT_AUTO_SIZE,
  flexGap: 8,
  flexWrap: 'nowrap',

  // FlexItem
  flexGrow: 0,
  flexShrink: 1,

  // Absolute
  // absoluteType: 'px', // ???
}

export const FIT_STYLES: Partial<NodeV2Style> = {
  position: 'absolute',
}

export const FIT_ELEMENTS: ElementsTypes[] = [
  CONFIG_ELEMENTS.image.type,
  CONFIG_ELEMENTS.labeledGraphic.type,
  CONFIG_ELEMENTS.simulator.type,
  CONFIG_ELEMENTS.testHotspot.type,
  CONFIG_ELEMENTS.splitter.type,
  CONFIG_ELEMENTS.rectangle.type,
  CONFIG_ELEMENTS.video.type,
  CONFIG_ELEMENTS.card.type,
]

export const genRootLayout = (nodes?: MapType<NodeV2>) => {
  const rootId = generateId()
  return {
    nodes: {
      ...nodes,
      [rootId]: genNode(rootId, {
        type: NodeType.node,
        children: [],
        isDone: false,
        style: {
          [DeviceMode.desktop]: {
            display: 'grid',
            width: saveSize({ value: 100, unit: '%' }),
            height: saveSize(null),
            gridColumns: saveGrid({
              min: 20,
              items: [FR_1, ...toArray(PX_60, 12), FR_1].map(toArrayValue),
            }),
            gridColumnsGap: 20,
            gridRowsGap: 8,
            gridRows: saveGrid({ min: 32, items: toArray(null, 16).map(toArrayValue) }),
            background: saveBackground({
              type: 'color',
              color: setThemeVariable('primaryBackgroundColor'),
              blendMode: 'normal',
            }),
            flexDirection: 'row',
          },
          [DeviceMode.tablet]: {
            gridColumns: saveGrid({
              min: 8,
              items: [PX_20, ...toArray(FR_1, 12), PX_20].map(toArrayValue),
            }),
            gridColumnsGap: 8,
          },
          [DeviceMode.mobile]: {
            gridColumns: saveGrid({
              min: 8,
              items: [PX_12, ...toArray(FR_1, 12), PX_12].map(toArrayValue),
            }),
            gridColumnsGap: 4,
          },
        },
      }),
    },
    rootId: rootId,
    version: 2,
  }
}

export const genSubLayout = (id: string, children: string[] = []): NodeV2 => ({
  id,
  type: NodeType.node,
  children,
  style: {
    [DeviceMode.desktop]: {
      display: 'grid',
      width: saveSize({ value: 100, unit: '%' }),
      gridColumns: saveGrid({ min: 32, items: toArray(FR_1, 4).map(toArrayValue) }),
      gridColumnsGap: 4,
      gridRows: saveGrid({ min: 32, items: toArray(null, 4).map(toArrayValue) }),
      gridRowsGap: 4,
    },
  },
})

export const genNode = (id: string, props?: Partial<NodeV2>): NodeV2 => ({
  id,
  type: NodeType.node,
  style: { [DeviceMode.desktop]: {} as NodeV2Style },
  children: [],
  ...props,
})
